"use client"

import { useState } from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

interface Session {
  id: string
  day: string
  title: string
  description: string
  topics: string[]
}

export function SessionsSection() {
  const [openSession, setOpenSession] = useState<string | null>(null)

  const sessions: Session[] = [
    {
      id: "1",
      day: "Day 1",
      title: "Interministerial Roundtable",
      description:
        "A high-level policy engineering chamber where ministers, DFIs, regulators, and corporate leaders co-design new national frameworks to unlock student financing, digital identity infrastructure, and youth credit inclusion, with immediate commitments expected.",
      topics: [
        "National frameworks for student financing",
        "Digital identity infrastructure development",
        "Youth credit inclusion strategies",
        "Immediate policy commitments and action plans",
      ],
    },
    {
      id: "2",
      day: "Day 1",
      title: "EduFund Launch",
      description:
        "The official unveiling of Africa's first decentralized education financing vehicle, a £1M pilot unlocking tuition co-funding, scholarships, and digital capital for students, inviting corporates, CSR leaders, and philanthropists to co-own the next chapter of accessible education finance.",
      topics: [
        "Decentralized education financing model",
        "£1M pilot program details",
        "Tuition co-funding and scholarship opportunities",
        "Corporate and philanthropic partnership opportunities",
      ],
    },
    {
      id: "3",
      day: "Day 1",
      title: "Partners Track",
      description:
        "A strategy and activation arena where universities, corporates, EdTechs, and fintechs launch products, run scholarship or funding announcements, test youth-market ideas live, and engage directly with policy, investment, and student decision-makers, with instant conversion opportunities.",
      topics: [
        "Product launches and live demonstrations",
        "Scholarship and funding announcements",
        "Youth-market testing and validation",
        "Direct engagement with decision-makers",
      ],
    },
    {
      id: "4",
      day: "Day 1",
      title: "Communiversity Launch",
      description:
        "The launch of a next-generation talent and venture pipeline that fast-tracks top students into high-growth careers or funded startups, giving sponsors first access to Africa's future workforce and founders before traditional markets begin competing for them.",
      topics: [
        "Talent pipeline development",
        "Career fast-tracking programs",
        "Startup funding opportunities",
        "Early access to top African talent",
      ],
    },
    {
      id: "5",
      day: "Day 1",
      title: "Pitch My Dissertation (PmD)",
      description:
        "Top African postgraduate researchers pitch commercialization-ready innovations, from AI to climate tech, offering investors, corporates, and DFIs first ownership access to patentable, fund-worthy intellectual breakthroughs before they hit the market.",
      topics: [
        "AI and technology innovations",
        "Climate tech solutions",
        "Commercialization strategies",
        "Investment and partnership opportunities",
      ],
    },
    {
      id: "6",
      day: "Day 1",
      title: "EdTech Africathon",
      description:
        "A high-intensity venture pitch arena showcasing Africa's most scalable digital learning technologies, where investors and corporates spot the next unicorns early and secure pilots, partnerships, or co-investment before the rest of the world catches on.",
      topics: [
        "Scalable EdTech solutions",
        "Digital learning innovations",
        "Pilot program opportunities",
        "Early-stage investment access",
      ],
    },
    {
      id: "7",
      day: "Day 1",
      title: "Student Recruitment LaunchPad",
      description:
        "A direct student recruitment and admissions marketplace where global universities access over 2,000 pre-qualified African students ready for scholarships and admissions, built for immediate conversion, not a passive expo experience.",
      topics: [
        "Direct access to 2,000+ qualified students",
        "Scholarship program promotion",
        "Admissions process streamlining",
        "Immediate student engagement",
      ],
    },
    {
      id: "8",
      day: "Day 1",
      title: "Africa Education Intelligence Report Launch",
      description:
        "The reveal of Edukate Africa's future-defining insight report, exposing real data on Africa's talent trends, AI-powered learning evolution, student finance behaviors, and future-of-work disruptions, built for CEOs, funders, and policymakers who need to act.",
      topics: [
        "Africa's talent trends and insights",
        "AI-powered learning evolution",
        "Student finance behavior analysis",
        "Future-of-work disruptions",
      ],
    },
    {
      id: "9",
      day: "Day 1",
      title: "Inter-University Debate Arena",
      description:
        "A fierce academic competition where top universities debate Africa's future across innovation, economy, and policy, spotlighting the continent's brightest strategic minds and creating a fast-track bridge into leadership, scholarships, and venture pipelines.",
      topics: [
        "Innovation and technology debates",
        "Economic policy discussions",
        "Leadership development opportunities",
        "Scholarship and venture pathways",
      ],
    },
  ]

  const toggleSession = (sessionId: string) => {
    setOpenSession(openSession === sessionId ? null : sessionId)
  }

  const renderSessions = (daySessions: Session[]) => (
    <div className="space-y-4">
      {daySessions.map((session) => (
        <div
          key={session.id}
          className="overflow-hidden rounded-xl border border-border bg-card transition-all hover:shadow-md"
        >
          <button
            onClick={() => toggleSession(session.id)}
            className="flex w-full items-start gap-4 p-6 text-left transition-colors hover:bg-muted/50"
          >
            <div className="flex-1">
              <h3 className="mb-1 text-xl font-semibold text-card-foreground">{session.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2">{session.description}</p>
            </div>
            <ChevronDown
              className={cn(
                "h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform",
                openSession === session.id && "rotate-180",
              )}
            />
          </button>

          {openSession === session.id && (
            <div className="border-t border-border bg-muted/30 p-6">
              <p className="mb-4 text-muted-foreground text-pretty">{session.description}</p>
              <div>
                <h4 className="mb-3 font-semibold text-card-foreground">Key Focus Areas:</h4>
                <ul className="space-y-2">
                  {session.topics.map((topic, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-secondary" />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )

  return (
    <section id="sessions" className="py-20 md:py-28">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl text-balance">
            Signature Tracks & Live Intelligence Platforms
          </h2>
          <p className="text-lg text-muted-foreground text-pretty">
            One day of high-impact sessions designed to unlock opportunities, forge partnerships, and shape Africa's
            education and digital economy future.
          </p>
        </div>

        <div className="mx-auto max-w-4xl space-y-12">
          <div>
            <h3 className="mb-6 text-2xl font-bold text-foreground">January 29, 2026</h3>
            {renderSessions(sessions)}
          </div>
        </div>
      </div>
    </section>
  )
}
